import {
  Heading,
  HeadingContainer,
  HeadingText,
  MainContent,
  TableContainer,
} from "./User.style";
import {
  Title,
  DynamicTitle,
  Header,
} from "../../../components/DashboardComps/Dashboard.styles";
import { COLUMNS } from "./columns";
import useUser from "../hooks/useUser";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

import { useFilterWrapper, useStoreWrapper } from "../../../store/store";

import StyledTable from "../../../components/StyledTable/StyledTable";
import { usePermissionCheck } from "../../../hooks/userPermissionCheck";
import { PERMISSION_LIST } from "../../../shared/sharedConstants";
import ActionBtnU from "./components/ActionBtnU";
import { IoMdArrowRoundBack } from "react-icons/io";

const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [currentOffset, setCurrentOffset] = useState(0);
  const [queryLimit, setQueryLimit] = useState(10);
  const [showAddNewUserBtn, setShowAddNewUserBtn] = useState<boolean>(false);
  const [facilitiesData, setFacilitiesData] = useState<any[]>();

  const { roles, setSelectedUser, userFacilities } = useStoreWrapper(
    (state: any) => ({
      roles: state.roles,
      setSelectedUser: state.setSelectedUser,
      userFacilities: state.userFacilities,
    })
  );

  const { roleFilter, userFacilityFilter } = useFilterWrapper((state) => ({
    roleFilter: state.roleFilter,
    userFacilityFilter: state.userFacilityFilter,
  }));

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [onUserList, setOnUserList] = useState(false);
  const [noOfPages, setNoOfPages] = useState<any>("");

  const { allUserData } = useUser({
    limit: queryLimit,
    offset: currentOffset,
    searchParam: debouncedSearchQuery,
    facility: userFacilityFilter,
    role: roleFilter,
  });

  useEffect(() => {
    refetch();
  }, [
    queryLimit,
    currentOffset,
    debouncedSearchQuery,
    roleFilter,
    userFacilityFilter,
  ]);

  usePermissionCheck(
    PERMISSION_LIST.ADD_USER,
    userFacilities,
    setShowAddNewUserBtn
  );

  const { isLoading, data: tableData, refetch, isFetching } = allUserData;

  useEffect(() => {
    if (tableData !== undefined) {
      // @ts-ignore
      setNoOfPages(Math.ceil(tableData?.usersCount / queryLimit));
    }
  }, [tableData]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 800);

    return () => clearTimeout(debounceTimeout);
  }, [searchQuery]);

  const handleNextPage = () => {
    let newOffset = currentOffset + queryLimit;
    // @ts-ignore
    if (newOffset > tableData?.usersCount) return;
    setCurrentOffset(newOffset);
  };

  const handlePreviousPage = () => {
    let newOffset = currentOffset - queryLimit;
    newOffset < 0 ? setCurrentOffset(0) : setCurrentOffset(newOffset);
  };
  const handlePageSize = (limit: number) => {
    setCurrentOffset(0);
    setQueryLimit(limit);
  };

  useMemo(() => {
    const _facilities = userFacilities?.map((facility: any) => {
      const { facilityName, facilityId } = facility;
      return {
        label: facilityName,
        value: facilityId,
      };
    });
    setFacilitiesData(_facilities);
  }, [userFacilities]);

  return (
    <MainContent>
      <HeadingContainer>
        {location.pathname === "/user/users" && <Heading>User List</Heading>}
        <HeadingText>
          Names and data of all users in your registered orgs and extensions
        </HeadingText>

        {location.pathname === "/user/dashboard/active-staff" && (
          <>
            <ActionBtnU
              borderStyle="1px solid rgb(181, 128, 255)"
              color="rgb(181, 128, 255)"
              onClick={() => window.history.back()}
            >
              <span className="mr-2 mt-1 font-bold">
                <IoMdArrowRoundBack />
              </span>
              Back
            </ActionBtnU>
            <Header>
              <Title>
                <Link to="/user/dashboard">Dashboard </Link>
                <DynamicTitle>/ User List</DynamicTitle>
              </Title>
            </Header>
          </>
        )}
      </HeadingContainer>
      <TableContainer>
        <StyledTable
          tableCols={COLUMNS}
          // @ts-ignore
          tableData={tableData?.users || []}
          showFilterComponent
          isLoading={isLoading}
          rolesData={roles
            ?.map((role: any) => role.Name)
            .sort((a: string, b: string) => (a > b ? 1 : -1))}
          facilitiesData={facilitiesData?.sort((a: any, b: any) =>
            a.label > b.label ? 1 : -1
          )}
          getUserCb={(item: any) => {
            setSelectedUser(item);
            navigate(`/user/users/user-management`);
          }}
          hasTag
          tagPosition={3}
          currentOffset
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          currentPage={Math.round(currentOffset / queryLimit)}
          // @ts-ignore
          noOfPages={Math.ceil(tableData?.usersCount / queryLimit) || 1}
          isFetching={isFetching}
          serverPaginated={!debouncedSearchQuery.length}
          goToFirstPage={() => setCurrentOffset(0)}
          goToLastPage={() => setCurrentOffset(queryLimit * (noOfPages - 1))}
          handlePageSize={handlePageSize}
          showAddNewUserBtn={showAddNewUserBtn}
          setSearchQuery={setSearchQuery}
          searchable
          onUserList={() => setOnUserList(!onUserList)}
          whereUsed="userList"
        />
      </TableContainer>
    </MainContent>
  );
};

export default Users;
